import type { Column, SortDirection } from '../simple-table'
import type { AdmissionAndProcedureClaim } from './types'

import { nanoid } from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { MdChevronRight } from 'react-icons/md'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { useDebounce } from 'react-use'

import http from '../../../lib/http'
import { useAppDispatch, useAppSelector } from '../../../store'
import { finishLoading, startLoading } from '../../../store/modules'
import { Filters } from '../components/filters'
import defaultStyles from '../default-table/index.module.css'
import { useDebouncedFilters } from '../hooks/use-debounced-filters'
import styles from '../providers-table/index.module.css'
import * as Table from '../simple-table'

import { admissionAndProcedureClaimsSchema } from './schema'
import { Status, statusNameMapper } from './status.enum'

const cptToCodeMap = [
  {
    display: 'inpatient_hospital',
    ranges: [
      { min: 99221, max: 99223 },
      { min: 99231, max: 99236 },
      { min: 99238, max: 99239 }
    ]
  },
  {
    display: 'ed',
    ranges: [{ min: 99281, max: 99288 }]
  },
  {
    display: 'inpatient_w_icu',
    ranges: [
      {
        min: 99291,
        max: 99292
      },
      {
        min: 99232,
        max: 99233
      }
    ]
  },
  {
    display: 'provider_appointment',
    ranges: [
      {
        min: 99201,
        max: 99205
      },
      {
        min: 99211,
        max: 99215
      }
    ]
  },
  {
    display: 'snf',
    ranges: [
      {
        min: 99304,
        max: 99310
      },
      {
        min: 99315,
        max: 99316
      }
    ]
  }
]

export const findCode = (code: string): string | undefined => {
  if (code.startsWith('Q')) {
    const n = parseInt(code.substring(1), 10)

    if (!isNaN(n) && n >= 5001 && n <= 5010) {
      return 'inpatient_hospital'
    }
  }

  if (code.startsWith('T')) {
    const n = parseInt(code.substring(1), 10)
    if (!isNaN(n) && n >= 2042 && n <= 2046) {
      return 'inpatient_hospital'
    }
  }

  try {
    const n = parseInt(code)
    return cptToCodeMap.find((el) =>
      el.ranges.some((i) => {
        return i.min <= n && i.max >= n
      })
    )?.display
  } catch (e) {
    console.error(e)
  }
}

interface ActionsProps {
  item: AdmissionAndProcedureClaim
  changeFormState: (table: string, value: AdmissionAndProcedureClaim) => void
  queryKey: QueryKey
  onAction?: () => Promise<unknown> | unknown
  onError?: (message: string) => Promise<unknown> | unknown
}

type HistoryAction = 'add' | 'ignore' | 'restore'

function Actions ({ item, queryKey, onAction, onError, changeFormState }: ActionsProps): JSX.Element | null {
  let patientId = useAppSelector(state => state.globalVars?.params?.id)
  let { status, procedure, service_date: date } = item
  let { code } = procedure ?? {}
  let resolvedCode = findCode(code ?? '')

  let dispatch = useAppDispatch()

  interface AddActionMutationPayload {
    action: 'add'
    code: string
    date: string
    resourceType: string
  }

  interface OtherActionMutationPayload {
    action: Exclude<HistoryAction, 'add'>
    code: string
    date: string
  }

  type MutationPayload =
    | AddActionMutationPayload
    | OtherActionMutationPayload

  let queryClient = useQueryClient()

  let mutation = useMutation({
    async mutationFn (payload: MutationPayload) {
      let { action, ...rest } = payload

      return await http.postRequest(`/patients/${patientId as string}/claims/admissions/${action}`, rest)
    },

    async onMutate (payload) {
      await queryClient.cancelQueries({
        predicate ({ queryKey }) {
          return Array.isArray(queryKey) && queryKey[0] === 'claims-history/providers'
        }
      })

      let previousQueryResponse = queryClient.getQueryData<Response>(queryKey)

      queryClient.setQueryData<Response>(queryKey, old => {
        if (!old) {
          return old
        }

        return {
          ...old,
          data: old.data.map(item => {
            if (item.procedure?.code === payload.code && item.service_date === payload.date) {
              return {
                ...item,
                status: payload.action === 'add'
                  ? Status.Current
                  : payload.action === 'ignore'
                    ? Status.Ignored
                    : payload.action === 'restore'
                      ? Status.New
                      : item.status
              }
            }

            return item
          })
        }
      })

      return {
        action: payload.action,
        previousQueryResponse
      }
    },

    onError (err, payload, context) {
      void err

      onError?.('Something went wrong.')
      queryClient.setQueryData(queryKey, context?.previousQueryResponse)
    },

    async onSettled () {
      onAction?.()
    }
  })

  useEffect(() => {
    if (mutation.isPending) {
      let id = nanoid()

      dispatch(startLoading({
        id,
        description: 'save admission claim'
      }))

      return () => {
        dispatch(finishLoading(id))
      }
    }
  }, [
    dispatch,
    mutation.isPending
  ])

  if (!patientId || !code || !date) {
    return null
  }

  return (
    <Table.ActionButtonWrapper>
      {status === 'new' && (
        <>
          <Table.ActionButtonDropdown
            buttonContent={(
              <>
                <button
                  type='button'
                  className={styles.suggestionButton}
                  onClick={() => {
                    changeFormState('admission', item)
                  }}
                >
                  <MdChevronRight
                    className={styles.suggestionArrow}
                    style={{ visibility: resolvedCode ? 'visible' : 'hidden' }}
                  />

                  <span>
                    Add to Admission Table
                  </span>
                </button>
                <button
                  type='button'
                  className={styles.suggestionButton}
                  onClick={() => {
                    changeFormState('procedure', item)
                  }}
                >
                  <MdChevronRight
                    className={styles.suggestionArrow}
                    style={{ visibility: resolvedCode ? 'hidden' : 'visible' }}
                  />

                  <span>
                    Add to Procedure Table
                  </span>
                </button>
              </>
            )}
          >
            <Table.ActionButton
              primary
              isLoading={mutation.isPending && mutation.context?.action === 'add'}
              isDisabled={mutation.isPending}
            >
              Add
            </Table.ActionButton>
          </Table.ActionButtonDropdown>

          <Table.ActionButton
            isLoading={mutation.isPending && mutation.context?.action === 'ignore'}
            isDisabled={mutation.isPending}
            onClick={() => {
              mutation.mutate({
                action: 'ignore',
                code: code as string,
                date: date as string
              })
            }}
          >
            Ignore
          </Table.ActionButton>
        </>
      )}

      {status === 'ignored' && (
        <Table.ActionButton
          isLoading={mutation.isPending && mutation.context?.action === 'restore'}
          isDisabled={mutation.isPending}
          onClick={() => {
            mutation.mutate({
              action: 'restore',
              code: code as string,
              date: date as string
            })
          }}
        >
          Restore
        </Table.ActionButton>
      )}
    </Table.ActionButtonWrapper>
  )
}

type QueryKey = [
  string,
  string,
  SearchParams
]

interface Response {
  data: AdmissionAndProcedureClaim[]
  total_filtered: number
  total_unfiltered: number
  filters: Record<string, Array<string | number>>
}

interface SearchParams extends Record<string, unknown>{
  query: string

  sort: {
    field: keyof AdmissionAndProcedureClaim
    direction: SortDirection
  }

  filters: {
    provider_name: string[]
    status: Status[]
  }

  pagination: {
    page: number
    pageSize: number
  }
}

let defaultSearchParams: SearchParams = {
  query: '',

  sort: {
    field: 'service_date',
    direction: 'desc'
  },

  filters: {
    provider_name: [],
    status: [
      Status.New
    ]
  },

  pagination: {
    page: 0,
    pageSize: 10
  }
}

function isDefaultSearchParams (params: SearchParams): boolean {
  return JSON.stringify(params) === JSON.stringify(defaultSearchParams)
}

interface AdmissionsClaimsTableProps {
  changeFormState: (table: string, value: AdmissionAndProcedureClaim) => void
}

export function AdmissionsAndProceduresClaimsTable ({ changeFormState }: AdmissionsClaimsTableProps): JSX.Element {
  let [
    debouncedSearchParams,
    searchParams,
    setSearchParams,
    setDebouncedSearchParams
  ] = useDebouncedFilters<SearchParams>(defaultSearchParams)

  let [ searchQuery, setSearchQuery ] = useState('')
  let client = useQueryClient()

  useDebounce(() => {
    setDebouncedSearchParams(params => ({
      ...params,
      query: searchQuery,
      pagination: {
        ...params.pagination,
        page: 0
      }
    }))
  }, 500, [ searchQuery ])


  let resource = useSelector<any, string>(state => state.globalVars?.params?.id)

  let queryKey = useMemo<QueryKey>(() => [
    'claims-history/admissions',
    resource,
    debouncedSearchParams
  ], [ resource, debouncedSearchParams ])

  let query = useQuery<Response, unknown, Response, QueryKey>({
    queryKey,
    async queryFn ({ queryKey }) {
      let [ , resource, searchParams ] = queryKey
      let { query, sort, filters, pagination } = searchParams
      let normalizedQuery = query.trim().toLowerCase()

      let urlSearchParams = new URLSearchParams([
        [ 'on', sort.field ],
        [ 'dir', sort.direction ],
        [ 'page', pagination.page.toString(10) ],
        [ 'size', pagination.pageSize.toString(10) ],
        ...filters.provider_name.map(p => [ 'provider_name', p ]),
        ...filters.status.map(s => [ 'status', s ])
      ])

      if (normalizedQuery.length > 0) {
        urlSearchParams.set('search', normalizedQuery)
      }

      let response: Response = await http.getRequest(`/patients/${resource}/claims/admissions?${urlSearchParams.toString()}`)
      let data = admissionAndProcedureClaimsSchema.parse(response.data)

      return {
        ...response,
        data
      }
    },
    enabled: typeof resource === 'string',
    retry: false,
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
    refetchOnReconnect: true,
    refetchOnWindowFocus: false
  })

  let { refetch } = query

  let handlePageChange = useCallback((value: number): void => {
    setDebouncedSearchParams(params => ({
      ...params,
      pagination: {
        ...params.pagination,
        page: value - 1
      }
    }))
  }, [ setDebouncedSearchParams ])

  let handlePageSizeChange = useCallback((value: number): void => {
    setDebouncedSearchParams(params => ({
      ...params,
      pagination: {
        ...params.pagination,
        page: 0,
        pageSize: value
      }
    }))
  }, [ setDebouncedSearchParams ])

  let handleSortChange = useCallback((field: keyof AdmissionAndProcedureClaim, direction: SortDirection): void => {
    setSearchParams(params => ({
      ...params,
      pagination: {
        ...params.pagination,
        page: 0
      },
      sort: {
        ...params.sort,
        field,
        direction
      }
    }))
  }, [ setSearchParams ])

  let handleFilterChange = useCallback((name: string, value: Array<string | number | boolean>): void => {
    setSearchParams(params => ({
      ...params,
      filters: {
        ...params.filters,
        [name]: value
      },
      pagination: {
        ...params.pagination,
        page: 0
      }
    }))
  }, [ setSearchParams ])

  let handleFiltersClear = useCallback((): void => {
    setSearchParams(params => ({
      ...params,
      pagination: {
        ...params.pagination,
        page: 0
      },
      filters: {
        ...params.filters,
        provider_name: [],
        status: []
      }
    }))
  }, [ setSearchParams ])

  let handleAction = useCallback(() => {
    void refetch()
  }, [ refetch ])

  let filters = useMemo(() => {
    let keys: Array<keyof SearchParams['filters']> = [
      'provider_name',
      'status'
    ]

    return keys.map(key => {
      let options = (query.data?.filters[key] ?? [])
        .map((value: string | number) => {
          let label = value

          if (key === 'status') {
            label = statusNameMapper[value as Status] ?? value
          }

          return {
            label: label.toString(),
            value
          }
        })

      return {
        name: key,
        value: searchParams.filters[key],
        options
      }
    })
  }, [ query.data?.filters, searchParams.filters ])

  let columns = useMemo<Array<Column<AdmissionAndProcedureClaim>>>(() => [
    {
      name: 'service_date',
      header: 'Service Date',
      getValue (item) {
        if (typeof item.service_date !== 'string') {
          return null
        }
        return Table.formatDate(item.service_date)
      },
      isSortable: true,
      gridArea: '0.7fr'
    },
    {
      name: 'place_of_service',
      header: 'Place of Service',
      isSortable: true
    },
    {
      gridArea: '0.7fr',
      name: 'procedure',
      header: 'Procedure',
      isSortable: true,
      getValue ({ procedure }) {
        if (!procedure) {
          return null
        }

        let { code, descriptor } = procedure

        return (
          <span>
            {typeof code === 'string' && (
              <>
                <Link
                  to={`../claims-history?procedure_code=${encodeURIComponent(code)}&force=procedure_code`}
                  className={styles.providerLink}
                >
                  <strong>{code}</strong>
                </Link>

                {' '}
              </>
            )}
            {descriptor}
          </span>
        )
      }
    },
    {
      name: 'provider_name',
      header: 'Provider Name',
      isSortable: true
    },
    {
      name: 'description',
      header: 'Code & Descriptor',
      gridArea: '2fr',
      getValue (item) {
        return (
          <div className={defaultStyles.description}>
            {item.description?.map(({ code, descriptor }, i) => (
              code ?? descriptor
                ? (
                  <div key={code ?? i}>
                    <strong>{code}</strong>
                    {' '}
                    {descriptor}
                  </div>)
                : null
            ))}
          </div>
        )
      }
    },
    {
      name: 'status',
      header: 'Status',
      isSortable: true,
      initialSortDirection: 'asc',
      getValue (item) {
        return typeof item.status === 'string'
          ? statusNameMapper[item.status] ?? '–'
          : '–'
      }
    },
    {
      name: 'actions',
      header: 'Action',
      getValue (item) {
        return (
          <Actions
            item={item}
            changeFormState={changeFormState}
            queryKey={queryKey}
            onAction={handleAction}
          />
        )
      }
    }

  ], [ changeFormState, queryKey, handleAction ])

  return (
    <Table.Table
      header={(
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
          <h2>Claims History | Admissions and Procedures</h2>
          {query.status === 'success' && (
            <div>
              <Filters
                search={searchQuery}
                onSearchChange={setSearchQuery}
              />
            </div>
          )}
        </div>
      )}
      columns={columns}
      data={query.data?.data ?? []}
      sort={searchParams.sort}
      pagination={{
        ...searchParams.pagination,
        totalFiltered: query.data?.total_filtered,
        totalUnfiltered: query.data?.total_unfiltered
      }}
      error={query.error as Error}
      queryStatus={query.status}
      fetchStatus={query.fetchStatus}
      onSortChange={handleSortChange}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      filters={filters}
      onFilterChange={handleFilterChange}
      onFiltersClear={handleFiltersClear}
      onRefetch={() => {
        if (isDefaultSearchParams(searchParams)) {
          return query.refetch()
        }

        client.invalidateQueries({
          queryKey: [
            'claims-history/admissions',
            resource
          ]
        })
          .then(() => {
            setSearchParams(defaultSearchParams)
          })
          .catch(err => {
            console.error(err)
            Sentry.captureException(err)
          })
      }}
    />
  )
}
